/**
 * **climax テーマの HUD**（Issue #549。手本はギルティギア Xrd の対戦画面）。
 *
 * ```
 * [顔]━━━━━━━━━━━━━━━━━━━▶ (紋章 3) ◀━━━━━━━━━━━━━━━━━━━[顔]
 *        8000                          8000
 *  名前                                                        名前
 *                            MAIN PHASE 1
 * ▯▯▯▯▯ ×5                                                ×4 ▯▯▯▯
 * ```
 *
 * - **LP のゲージ**は上の端（顔）から中央へ伸びる細長い帯で、中央側が尖る。削られると**中央側から**縮み、
 *   削れた分は赤い残像（`lifeGhost`）が遅れて縮む。ピンチ（`lowLife` 以下）で色が赤に変わる
 * - **中央の紋章**にターン数（赤く光る数字）。下にフェイズ名
 * - **手札は残機のように**顔の下にスリーブの画像を少しずつ重ねて枚数ぶん並べる（ユーザー判断 2026-09-25）
 * - **LP の数字**はゲージの中央側の端の下（ギルティギアには無いが、デュエルでは要る）
 * - 金属の装飾は `hud/ornaments.js`（**差し替えられる**。生成用のプロンプトも同じ所）
 * - 演出が当てる作用（`fx`）: LP のゲージが揺れて光る、紋章が脈打つ。**HUD は時計を持たない**（揺れは `fx.phase` の関数）
 *
 * 文字は 46px 以上（`core/hud::MIN_FONT`）。CSS に transition / animation は書かない（`purity.test.js`）。
 */
import { withDefaults } from '../../core/theme';
import { INK_CLASS, strokeCss } from '../../core/decor';
import { MIN_FONT, hudFxOf, mirrorBox, partCss, partStyle } from '../../core/hud';
import { Ornament, ornamentSpec, windowClip } from './hud/ornaments';
import './climax.css';

export const HUD_PARTS = Object.freeze([
  { part: 'hud:portrait', label: 'HUD / 顔', space: 'screen' },
  { part: 'hud:bar', label: 'HUD / LP のゲージ', space: 'screen' },
  { part: 'hud:name', label: 'HUD / 名前', space: 'screen', decor: ['stroke'] },
  { part: 'hud:life', decor: ['stroke'] },
  { part: 'hud:hand', decor: ['stroke'] },
  { part: 'hud:turnBadge', decor: ['stroke'] },
].map((x) => Object.freeze(x)));

export const HUD_SCHEMA = Object.freeze({
  barTop: { type: 'number', label: 'ゲージの上からの距離', min: 0, max: 200, step: 1, part: 'hud:bar' },
  barGap: { type: 'number', label: 'ゲージと紋章の間', min: -60, max: 120, step: 1, part: 'hud:bar' },
  portraitW: { type: 'number', label: '顔の幅（高さは装飾の縦横比）', min: 100, max: 360, step: 1, part: 'hud:portrait' },
  side: { type: 'number', label: '端からの距離', min: 0, max: 200, step: 1, part: 'hud:portrait' },
  emblemW: { type: 'number', label: '紋章の幅（高さは装飾の縦横比）', min: 200, max: 800, step: 1, part: 'hud:turnBadge' },
  turnFont: { type: 'number', label: 'ターン数の文字', min: MIN_FONT, max: 140, step: 1, part: 'hud:turnBadge' },
  lifeFont: { type: 'number', label: 'LP の文字', min: MIN_FONT, max: 120, step: 1, part: 'hud:life' },
  nameFont: { type: 'number', label: '名前の文字（下限 46px の例外）', min: 24, max: 90, step: 1, part: 'hud:name' },
  lifeMax: { type: 'number', label: 'ゲージの満タンの LP', min: 1000, max: 16000, step: 100, part: 'hud:bar' },
  lowLife: { type: 'number', label: 'ピンチの LP（数字が赤くなる）', min: 0, max: 8000, step: 100, part: 'hud:bar' },
  // **ゲージの色は LP の減り具合で段階的に変わる**（青 → 緑 → 黄 → 赤。満タンに対する割合のしきい値）
  lifeBlue: { type: 'color', label: 'ゲージの色（多い）', part: 'hud:bar' },
  lifeGreen: { type: 'color', label: 'ゲージの色（中）', part: 'hud:bar' },
  lifeYellow: { type: 'color', label: 'ゲージの色（少ない）', part: 'hud:bar' },
  lifeRed: { type: 'color', label: 'ゲージの色（わずか）', part: 'hud:bar' },
  lifeToGreen: { type: 'number', label: '緑になる割合', min: 0, max: 1, step: 0.05, part: 'hud:bar' },
  lifeToYellow: { type: 'number', label: '黄になる割合', min: 0, max: 1, step: 0.05, part: 'hud:bar' },
  lifeToRed: { type: 'number', label: '赤になる割合', min: 0, max: 1, step: 0.05, part: 'hud:bar' },
  handCardW: { type: 'number', label: '手札のスリーブの幅', min: 24, max: 120, step: 1, part: 'hud:hand' },
  handStep: { type: 'number', label: '手札のスリーブの間隔', min: 6, max: 80, step: 1, part: 'hud:hand' },
  handGap: { type: 'number', label: '顔と手札の間', min: -40, max: 80, step: 1, part: 'hud:hand' },
  handMax: { type: 'number', label: '並べる上限（超えたら数字だけ）', min: 1, max: 20, step: 1, part: 'hud:hand' },
  shakePx: { type: 'number', label: '揺れの大きさ（px）', min: 0, max: 60, step: 1, part: 'hud:bar' },
});

export const HUD_STYLE = Object.freeze({
  barTop: 10, barGap: 40,
  portraitW: 210, side: 0,
  emblemW: 440, turnFont: 84,
  lifeFont: 72, nameFont: 36,
  lifeMax: 8000, lowLife: 2000,
  lifeBlue: '#2f8fff', lifeGreen: '#35d45a', lifeYellow: '#ffd21f', lifeRed: '#ff2d2d',
  lifeToGreen: 0.75, lifeToYellow: 0.5, lifeToRed: 0.25,
  handCardW: 44, handStep: 24, handGap: 8, handMax: 10,
  shakePx: 22,
  'dc/hud:name/strokeW': 8,
  'dc/hud:life/strokeW': 10,
  'dc/hud:hand/strokeW': 7,
  'dc/hud:turnBadge/strokeW': 8,
});

export const hudStyle = withDefaults(() => [HUD_STYLE]);
const font = (n) => Math.max(MIN_FONT, n);

/**
 * **P1 の側の配置**（舞台のピクセル。P2 は左右を鏡に写す）。
 * 顔は左上の角、ゲージは顔の中ほどから紋章の手前まで、名前はゲージの下の外側、LP はゲージの下の中央側。
 */
export function layoutP1(canvas, style) {
  const v = hudStyle(style);
  // **箱の縦横比は装飾が名乗る**（差し替えた画像ごとに違う。`hud/ornaments.js::ornamentSpec`）
  const portrait = { x: v.side, y: v.side, w: v.portraitW, h: Math.round(v.portraitW / ornamentSpec('portraitFrame').aspect) };
  const barX = portrait.x + portrait.w * 0.72;
  const barEnd = canvas.w / 2 - v.emblemW / 2 + v.barGap;
  const barW = Math.max(80, barEnd - barX);
  const bar = { x: barX, y: v.barTop, w: barW, h: Math.round(barW / ornamentSpec('barFrame').aspect) };
  const lifeSize = font(v.lifeFont);
  const lifeW = Math.round(lifeSize * 3.1);
  // LP の数字はゲージの下の外側（顔のすぐ右。ユーザー判断 2026-09-25）
  const life = { x: portrait.x + portrait.w + 8, y: bar.y + bar.h - 2, w: lifeW, h: Math.round(lifeSize * 1.1) };
  /*
   * 名前は顔と手札の間。**文字の下限 46px（`core/hud::MIN_FONT`・Issue #493）の例外**
   * —— ユーザー判断 2026-09-25「もう少し小さく」。ほかの文字は 46px 以上のまま
   */
  const nameSize = Math.max(24, v.nameFont);
  const name = { x: portrait.x + 12, y: portrait.y + portrait.h + 2, size: nameSize, w: portrait.w + 120 };
  // 手札（名前の下に左から並べる。箱は上限の枚数ぶん ＋ 枚数の文字）
  const cardH = Math.round(v.handCardW / 0.686);
  const hand = {
    x: portrait.x + 12, y: name.y + Math.round(nameSize * 1.2) + v.handGap,
    w: v.handCardW + v.handStep * (v.handMax - 1) + font(46) * 2.2, h: cardH, cardW: v.handCardW, cardH, step: v.handStep, max: v.handMax,
  };
  return { portrait, bar, life, name, hand, lifeSize };
}

const mirror = (box, canvas) => mirrorBox(box, canvas, 'mirror');

/** ターンの紋章の箱（上の中央） */
export function emblemBox(canvas, style) {
  const v = hudStyle(style);
  const h = Math.round(v.emblemW / ornamentSpec('emblem').aspect);
  return { x: canvas.w / 2 - v.emblemW / 2, y: 4, w: v.emblemW, h };
}

export function anchors({ canvas, style }) {
  const p1 = layoutP1(canvas, style);
  const pick = (p, box) => (p === 1 ? mirror(box, canvas) : box);
  const emb = emblemBox(canvas, style);
  return {
    /** 枠 ＝ 顔からゲージの端までの帯（演出が「その人の枠」を光らせるときの場所） */
    plate: (p) => pick(p, { x: p1.portrait.x, y: p1.portrait.y, w: p1.bar.x + p1.bar.w - p1.portrait.x, h: p1.portrait.h }),
    /** LP の数字の箱（ダメージの数字が飛ぶ先） */
    life: (p) => pick(p, p1.life),
    /** 手札の並び（x, y は中心。ドローの着地点） */
    hand: (p) => {
      const b = pick(p, p1.hand);
      // 並べ方（スリーブの幅・ずらし・上限。外側の端から並ぶ）も返す（ドローが「次の 1 枚」の場所へ飛ぶ。Issue #582）
      return {
        x: b.x + b.w / 2, y: b.y + b.h / 2, w: b.w, h: b.h, cardW: b.cardW, cardH: b.cardH, step: b.step, max: b.max,
      };
    },
    /** **x は中心**（`core/hud` の約束。`hand` と同じく中心で持つ口） */
    turnBadge: () => ({ x: emb.x + emb.w / 2, y: emb.y, w: emb.w, h: emb.h, baseH: emb.h }),
  };
}

/* ------------------------------------------------------------------ *
 *  絵（DOM）
 * ------------------------------------------------------------------ */

/** 揺れのずらし（**`phase` の関数**。同じフレームなら同じ位置） */
export function shakeOffset(fx, px) {
  if (!(fx?.shake > 0)) return 0;
  const t = fx.phase ?? 0;
  return (Math.sin(t * 0.083) * 0.7 + Math.sin(t * 0.211) * 0.3) * px * fx.shake;
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/**
 * **登場の出方**（`appear` 0〜1。Issue #646。climax の `start` が HUD を組み上げる）。1 なら素のまま（空）。
 * 進みのイージングは演出が決める（ここは進みを位置・切り抜きに写すだけ）。0 なら隠す。
 * @returns {{ transform?: string, clipPath?: string, opacity?: number, visibility?: string }}
 */
export function appearStyle(kind, a, right = false) {
  const k = clamp01(Number.isFinite(a) ? a : 1);
  if (k >= 1) return {};
  if (k <= 0) return { visibility: 'hidden' };
  const r = 1 - k;
  const sx = right ? 1 : -1;   // 外側の向き
  switch (kind) {
    case 'portrait': return { transform: `translate(${(sx * 380 * r).toFixed(1)}px, ${(-260 * r).toFixed(1)}px)` };
    // 顔の枠の側（外側）から中央へ伸びる
    case 'bar': return { clipPath: right ? `inset(0 0 0 ${(r * 100).toFixed(2)}%)` : `inset(0 ${(r * 100).toFixed(2)}% 0 0)` };
    case 'drop': return { transform: `translateY(${(-44 * r).toFixed(1)}px)`, opacity: k };
    case 'emblem': return { transform: `translateY(${(-320 * r).toFixed(1)}px) rotate(${(180 * r).toFixed(1)}deg)` };
    default: return { opacity: k };
  }
}
/** transform を重ねる（どちらか無ければもう一方） */
const joinTransform = (...ts) => ts.filter(Boolean).join(' ') || undefined;
/** 登場の出方 ＋ 要素の transform（揺れなど）＋ 部品の調整（`partCss`）を 1 つの style に（transform を潰し合わない） */
const withAppear = (style, part, ap, extra = null) => {
  const base = joinTransform(ap.transform, extra);
  return { ...ap, transform: base, ...(partCss(style, part, base) ?? {}) };
};

/** **LP の割合 → ゲージの色**（青 → 緑 → 黄 → 赤。しきい値と色は `style`） */
export function lifeColor(ratio, style) {
  const v = hudStyle(style);
  if (ratio > v.lifeToGreen) return v.lifeBlue;
  if (ratio > v.lifeToYellow) return v.lifeGreen;
  if (ratio > v.lifeToRed) return v.lifeYellow;
  return v.lifeRed;
}

/** HUD の下端の線（顔の枠と LP の数字の下端の大きいほう）。フェイズ名をここに収める */
export function hudBaseline(canvas, style) {
  const p1 = layoutP1(canvas, style);
  return Math.max(p1.portrait.y + p1.portrait.h, p1.life.y + p1.life.h);
}
const portraitImage = (player) => player?.deckImage || player?.image || null;

/**
 * **1 人ぶん**（顔・ゲージ・名前・LP・手札）。P2 は箱を鏡に写し、装飾は左右反転する。
 * ゲージは**外側（顔の側）に寄せて**、中央側から縮む。
 */
function Side({ index, values, canvas, style, fx }) {
  const v = hudStyle(style);
  const right = index === 1;
  const p1 = layoutP1(canvas, style);
  const box = (b) => (right ? mirror(b, canvas) : b);
  const player = values.players[index] ?? {};
  const lifeFx = hudFxOf(fx, 'life', index);
  // 演出が「この間はこの値で見せる」と頼んだら、数字もゲージもその値で（ダメージの数字が着くまで減る前の LP。Issue #554）
  const held = Number.isFinite(lifeFx.life) ? lifeFx.life : null;
  const life = held ?? values.life[index];
  /*
   * 残像（赤）は「減った分」なので、見せる値が本来の値より上（ダメージの数字が着く前）なら残像も少なくともそこまで、
   * **本来の値より下（start の 0 → 開始 LP の充填・回復の前）なら残像は出さない**（まだ起きていない分を赤で先に見せない。Issue #646）
   */
  const ghost = held == null ? values.lifeGhost[index] : (held < values.life[index] ? held : Math.max(values.lifeGhost[index], held));
  const active = values.turn > 0 && values.turnPlayer === index;
  const low = life <= v.lowLife;
  const plateFx = hudFxOf(fx, 'plate', index);
  const handFx = hudFxOf(fx, 'hand', index);
  const apPortrait = appearStyle('portrait', plateFx.appear, right);
  const apBar = appearStyle('bar', lifeFx.appear, right);
  const apLife = appearStyle('fade', lifeFx.appear, right);
  const apDrop = appearStyle('drop', handFx.appear, right);
  const shake = shakeOffset(lifeFx, v.shakePx) + shakeOffset(plateFx, v.shakePx);
  const flash = Math.max(lifeFx.flash, plateFx.flash);
  const flashColor = lifeFx.color ?? plateFx.color ?? '#ff2e3c';
  const bar = box(p1.bar);
  // **ゲージの窓は装飾が名乗る形**（枠の画像の中の透明な部分。P2 は左右反転）
  const barSpec = ornamentSpec('barFrame');
  const winPts = barSpec.window ?? [[0, 0], [1, 0], [1, 1], [0, 1]];
  const wl = Math.min(...winPts.map((q) => q[0]));
  const wr = Math.max(...winPts.map((q) => q[0]));
  const fillW = clamp01(life / v.lifeMax);
  const ghostW = clamp01(ghost / v.lifeMax);
  const clip = windowClip(winPts, right);
  const span = (k) => ({ [right ? 'right' : 'left']: `${wl * 100}%`, width: `${(wr - wl) * k * 100}%` });
  const image = portraitImage(player);
  const lifeBox = box(p1.life);
  const nameBox = box({ x: p1.name.x, y: p1.name.y, w: p1.name.w, h: Math.round(p1.name.size * 1.25) });
  const hand = box(p1.hand);
  const handCount = values.hands[index] ?? 0;
  const sleeve = player.sleeveImage || null;
  const moveX = shake ? `translateX(${shake.toFixed(2)}px)` : undefined;
  const outSide = right ? 'right' : 'left';
  const inSide = right ? 'left' : 'right';
  return (
    <div className={`cx-side cx-side--${right ? 'right' : 'left'}${active ? ' is-turn' : ''}${low ? ' is-low' : ''}`} style={{ '--player-color': player.color }} data-testid={`hud-plate-${index}`}>
      {/* LP のゲージ（中身 → 枠の順に重ねる） */}
      <div className="cx-bar" style={{ ...partStyle(bar), ...withAppear(style, 'hud:bar', apBar, moveX) }} data-testid={`hud-bar-${index}`}>
        <div className="cx-bar__window" style={{ clipPath: clip }}>
          <div className="cx-bar__ghost" style={span(ghostW)} />
          <div className="cx-bar__fill" style={{ ...span(fillW), '--life-color': lifeColor(fillW, style) }} data-life-color={lifeColor(fillW, style)} />
          <div className="cx-bar__shine" />
          {flash > 0 ? <div className="cx-flash" style={{ background: flashColor, opacity: Math.min(0.9, flash) }} /> : null}
        </div>
        <Ornament name="barFrame" flip={right} />
      </div>

      {/* 顔（絵 → 枠） */}
      <div className="cx-portrait" style={{ ...partStyle(box(p1.portrait)), ...withAppear(style, 'hud:portrait', apPortrait, moveX) }}>
        <div className="cx-portrait__img" style={{ backgroundImage: image ? `url("${image}")` : undefined, clipPath: windowClip(ornamentSpec('portraitFrame').window, right) }}>
          {image ? null : `${index + 1}P`}
        </div>
        <Ornament name="portraitFrame" flip={right} />
      </div>

      {/* 名前（ゲージの下の外側） */}
      <div className={`cx-name ${INK_CLASS}`} style={{ ...partStyle(nameBox), fontSize: p1.name.size, textAlign: outSide, ...strokeCss(style, 'hud:name'), ...withAppear(style, 'hud:name', apDrop) }}>
        {player.name || player.deckName || `${index + 1}P`}
      </div>

      {/* LP の数字（ゲージの下の中央側） */}
      <div
        className={`cx-life ${INK_CLASS}`}
        style={{
          ...partStyle(lifeBox), fontSize: p1.lifeSize, textAlign: outSide,
          textShadow: flash > 0 ? `0 0 ${Math.round(30 * flash)}px ${flashColor}` : undefined,
          ...strokeCss(style, 'hud:life'), ...withAppear(style, 'hud:life', apLife, moveX),
        }}
        data-testid={`hud-lp-${index}`}
      >
        {Math.round(life)}
      </div>

      {/* 手札（残機のように、顔の下にスリーブを重ねて並べる） */}
      <div className="cx-hand" style={{ ...partStyle(hand), ...withAppear(style, 'hud:hand', apDrop) }} data-testid={`hud-hand-${index}`}>
        {Array.from({ length: Math.min(handCount, p1.hand.max) }, (_, i) => (
          <div
            key={i}
            className="cx-hand__card"
            style={{
              [outSide]: i * p1.hand.step, top: 0, width: p1.hand.cardW, height: p1.hand.cardH,
              backgroundImage: sleeve ? `url("${sleeve}")` : undefined,
              zIndex: i,
            }}
            data-testid={`hud-hand-card-${index}`}
          />
        ))}
        <div
          className={`cx-hand__count ${INK_CLASS}`}
          style={{ [outSide]: p1.hand.cardW + p1.hand.step * Math.max(0, Math.min(handCount, p1.hand.max) - 1) + 10, fontSize: font(46), ...strokeCss(style, 'hud:hand') }}
        >
          ×{handCount}
        </div>
      </div>
    </div>
  );
}

/** 中央の紋章（ターン数）とフェイズ名 */
function Emblem({ values, canvas, style, fx }) {
  const v = hudStyle(style);
  const b = emblemBox(canvas, style);
  const color = values.players[values.turnPlayer]?.color ?? '#888';
  const scale = 1 + (fx?.pulse ?? 0) * 0.1;
  const battle = /BATTLE/i.test(values.phaseLabel ?? '');
  const ap = appearStyle('emblem', fx?.appear);
  return (
    <div className="cx-emblem" style={{ ...partStyle(b), '--player-color': color, ...withAppear(style, 'hud:turnBadge', ap, scale !== 1 ? `scale(${scale.toFixed(3)})` : null) }} data-testid="hud-turntag">
      <Ornament name="emblem" />
      <div className={`cx-emblem__turn ${INK_CLASS}`} style={{ top: `${(ornamentSpec('emblem').number?.y ?? 0.45) * 100}%`, fontSize: font(v.turnFont), ...strokeCss(style, 'hud:turnBadge') }}>
        {values.turn > 0 ? values.turn : '-'}
      </div>
      {fx?.flash > 0 ? <div className="cx-emblem__flash" style={{ background: fx.color ?? '#ffffff', opacity: Math.min(0.8, fx.flash * 0.8) }} /> : null}
      {values.phaseLabel ? (
        <div
          className={`cx-emblem__phase ${INK_CLASS}${battle ? ' is-battle' : ''}`}
          // **下端を顔の枠・LP の数字の下端の線に揃える**（紋章の箱からの相対）
          style={{ top: hudBaseline(canvas, style) - b.y - font(46) * 1.1, fontSize: font(46), ...strokeCss(style, 'hud:turnBadge') }}
          data-testid="hud-phase"
        >
          {values.phaseLabel}
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param fx 演出が当てる作用（`core/hud::foldHudFx`）
 * @param canvas 舞台の大きさ（`HudLayer` が渡す）
 */
export default function ClimaxHud({ values, style, fx = {}, canvas = { w: 1920, h: 1080 } }) {
  return (
    <div className="de-hud-climax">
      {[0, 1].map((i) => <Side key={i} index={i} values={values} canvas={canvas} style={style} fx={fx} />)}
      <Emblem values={values} canvas={canvas} style={style} fx={hudFxOf(fx, 'turnBadge')} />
    </div>
  );
}
